import { ColorPicker } from '@/components/ColorPicker';
import { isValidHexColor } from '@/components/ColorPicker/utils/isValidHexColor';
import { predefinedColors } from '@/pages/AdvancedEditor/components/panels/common/predefinedColors';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { polotnoTypes } from '@/pages/AdvancedEditor/types';
import { checkPolotnoType } from '@/pages/AdvancedEditor/utils';
import theme from '@/theme';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { ElementType } from 'polotno/model/group-model';

const ColorPickerContainer = styled.div`
    display: flex;
    width: min-content;
    gap: 8px;
    align-items: center;
    cursor: pointer;
    border-radius: 8px;
    padding: 6px 8px;

    color: ${theme.colors.secondary.main};
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;

    border: 1px solid ${theme.colors.black[200]};
`;

const InvisibleInput = styled.input`
    border: none;
    outline: none;
    width: auto;
    min-width: 0;
    flex: 0 1 auto;

    color: ${theme.colors.secondary.main};
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
`;

const INITIAL_FIGURE_BORDER_COLOR = '#0c0c0c';
const DEFAULT_FIGURE_BORDER_COLOR = '#000000';

export const initColorState = (color: string) => (color === INITIAL_FIGURE_BORDER_COLOR ? DEFAULT_FIGURE_BORDER_COLOR : color);

export const getBorderColorField = (element: ElementType) => (checkPolotnoType(element, polotnoTypes.Line) ? 'color' : 'stroke');

export const BorderColorControl = observer(() => {
    const { store } = useAdvancedEditorContext();
    const elements = store.selectedShapes;
    const element = elements[0] as ElementType & { stroke: string; color: string };

    if (!element) return null;

    const colorField = getBorderColorField(element);
    const color = element[colorField];

    const handleChange = (color: string) => {
        elements.forEach(element => element.set({ [getBorderColorField(element)]: color }));
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value.replaceAll('#', '');
        if (isValidHexColor(input)) {
            handleChange('#' + input);
        }
    };

    return (
        <ColorPickerContainer>
            <ColorPicker value={color} onChange={handleChange} size="24px" predefinedColors={predefinedColors} />
            <InvisibleInput size={8} type="text" value={color} onChange={handleInputChange} onClick={e => e.currentTarget.select()} />
        </ColorPickerContainer>
    );
});
